/** @jsxImportSource react */
import React from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '../components/ui/Toast'
import { api } from '../services/api'
import { useTranslation } from '../i18n'
import { NotificationPreference } from '../types'
import {
  Bell,
  Mail,
  MessageSquare,
  AlertTriangle,
  CheckCircle,
  X,
  Loader2,
  Save,
  FileText,
  UserPlus,
} from 'lucide-react'

const getPreferenceIcon = (id: string) => {
  switch (id) {
    case 'order_updates':
      return <Bell size={20} />
    case 'quote_received':
      return <FileText size={20} />
    case 'payment_confirmations':
      return <CheckCircle size={20} />
    case 'shipping_alerts':
      return <AlertTriangle size={20} />
    case 'new_user_signup':
      return <UserPlus size={20} />
    default:
      return <Bell size={20} />
  }
}

const NotificationPreferencesPage = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { data: preferences = [], isLoading } = useQuery({
    queryKey: ['notificationPreferences'],
    queryFn: () => api.getNotificationPreferences(),
  })
  const [localPreferences, setLocalPreferences] = React.useState<
    NotificationPreference[]
  >([])
  React.useEffect(() => {
    if (preferences.length > 0) {
      setLocalPreferences(JSON.parse(JSON.stringify(preferences))) // Deep copy
    }
  }, [preferences])
  const updateMutation = useMutation({
    mutationFn: (updatedPreferences: NotificationPreference[]) =>
      api.updateNotificationPreferences(updatedPreferences),
    onSuccess: () => {
      toast.success(t('preferencesSaved') || 'Preferences saved successfully!')
      queryClient.invalidateQueries({
        queryKey: ['notificationPreferences'],
      })
    },
    onError: () => {
      toast.error(t('preferencesError') || 'Failed to save preferences.')
    },
  })
  const handleToggle = (
    id: string,
    type: 'email' | 'sms',
    enabled: boolean,
  ) => {
    setLocalPreferences((prev) =>
      prev.map((p) =>
        p.id === id
          ? {
              ...p,
              [`${type}Enabled`]: enabled,
            }
          : p,
      ),
    )
  }
  const handleSaveChanges = () => {
    updateMutation.mutate(localPreferences)
  }
  const hasChanges =
    JSON.stringify(preferences) !== JSON.stringify(localPreferences)
  // Loading skeleton
  if (isLoading) {
    return (
      <div className="w-full bg-white min-h-screen">
        <div className="container mx-auto px-4 py-6">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-secondary-100/70 animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
              <div className="animate-pulse">
                <div className="h-8 w-64 bg-secondary-200 rounded mb-2"></div>
                <div className="h-4 w-96 bg-secondary-200 rounded"></div>
              </div>
              <div className="mt-4 sm:mt-0">
                <div className="h-10 w-32 bg-secondary-200 rounded"></div>
              </div>
            </div>
            <div className="space-y-6">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="bg-white border border-secondary-200 rounded-xl p-6 animate-pulse"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-2.5 bg-secondary-200 rounded-lg h-10 w-10"></div>
                    <div className="flex-1">
                      <div className="h-6 bg-secondary-200 rounded w-48 mb-2"></div>
                      <div className="h-4 bg-secondary-200 rounded w-full mb-4"></div>
                      <div className="flex flex-wrap gap-6">
                        <div className="flex items-center gap-3">
                          <div className="h-6 w-12 bg-secondary-200 rounded-full"></div>
                          <div className="h-4 w-12 bg-secondary-200 rounded"></div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="h-6 w-12 bg-secondary-200 rounded-full"></div>
                          <div className="h-4 w-12 bg-secondary-200 rounded"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }
  return (
    <div className="w-full bg-white min-h-screen">
      <div className="container mx-auto px-4 py-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-secondary-100/70 animate-fade-in">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-secondary-900 mb-2">
                {t('notificationPreferences') || 'Notification Preferences'}
              </h1>
              <p className="text-secondary-600 max-w-2xl">
                {t('notificationPreferencesDesc') ||
                  'Configure how and when you receive notifications from the system.'}
              </p>
            </div>
            <div className="mt-4 sm:mt-0">
              <button
                onClick={handleSaveChanges}
                disabled={!hasChanges || updateMutation.isPending}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-all duration-200 ${hasChanges ? 'bg-primary-500 hover:bg-primary-600 text-white' : 'bg-secondary-100 text-secondary-400 cursor-not-allowed'}`}
              >
                {updateMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>{t('saving') || 'Saving...'}</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>{t('saveChanges') || 'Save Changes'}</span>
                  </>
                )}
              </button>
            </div>
          </div>
          {/* Changes indicator */}
          {hasChanges && (
            <div className="mb-6 p-3 bg-primary-50 border border-primary-200 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-2 text-primary-700">
                <AlertTriangle className="w-5 h-5" />
                <span className="text-sm font-medium">
                  {t('unsavedChanges') || 'You have unsaved changes'}
                </span>
              </div>
              <button
                onClick={() =>
                  setLocalPreferences(JSON.parse(JSON.stringify(preferences)))
                }
                className="text-primary-700 hover:text-primary-800 text-sm font-medium flex items-center gap-1"
              >
                <X className="w-4 h-4" />
                <span>{t('discardChanges') || 'Discard'}</span>
              </button>
            </div>
          )}
          <div className="space-y-6">
            {localPreferences.map((pref) => (
              <div
                key={pref.id}
                className="bg-white border border-secondary-200 rounded-xl p-6 transition-all duration-200 hover:shadow-md hover:border-primary-300"
              >
                <div className="flex flex-col sm:flex-row items-start gap-6">
                    {/* Description */}
                    <div className="flex-1 flex items-start gap-4">
                        <div className="p-2.5 bg-primary-50 text-primary-600 rounded-lg flex-shrink-0">
                            {getPreferenceIcon(pref.id)}
                        </div>
                        <div className="text-start">
                            <h3 className="font-semibold text-lg text-secondary-800 mb-1">
                                {t(`pref_${pref.id}_name`) || pref.name}
                            </h3>
                            <p className="text-sm text-secondary-600">
                                {t(`pref_${pref.id}_desc`) || pref.description}
                            </p>
                        </div>
                    </div>
                    {/* Controls */}
                    <div className="flex flex-row sm:flex-col gap-4 sm:gap-3 pt-4 sm:pt-0 sm:ps-6 sm:border-s sm:border-secondary-200 w-full sm:w-auto rtl:sm:border-s-0 rtl:sm:border-e rtl:sm:ps-0 rtl:sm:pe-6">
                        {/* Email Checkbox */}
                        <label htmlFor={`email-${pref.id}`} className="flex items-center gap-3 cursor-pointer p-2 rounded-md hover:bg-secondary-50 transition-colors w-full sm:w-auto">
                            <input
                                type="checkbox"
                                id={`email-${pref.id}`}
                                checked={pref.emailEnabled}
                                onChange={(e) => handleToggle(pref.id, 'email', e.target.checked)}
                                className="h-5 w-5 rounded border-secondary-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                            />
                            <div className="flex items-center gap-1.5">
                                <Mail className="w-4 h-4 text-secondary-500" />
                                <span className="text-sm font-medium text-secondary-700">{t('email')}</span>
                            </div>
                        </label>
                        {/* SMS Checkbox */}
                        <label htmlFor={`sms-${pref.id}`} className="flex items-center gap-3 cursor-pointer p-2 rounded-md hover:bg-secondary-50 transition-colors w-full sm:w-auto">
                            <input
                                type="checkbox"
                                id={`sms-${pref.id}`}
                                checked={pref.smsEnabled}
                                onChange={(e) => handleToggle(pref.id, 'sms', e.target.checked)}
                                className="h-5 w-5 rounded border-secondary-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                            />
                            <div className="flex items-center gap-1.5">
                                <MessageSquare className="w-4 h-4 text-secondary-500" />
                                <span className="text-sm font-medium text-secondary-700">{t('sms')}</span>
                            </div>
                        </label>
                    </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
export default NotificationPreferencesPage